import React, { ChangeEvent, FC, useState } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import Input from "components/Input/Input";
import { useFormik } from "formik";
import { Col } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import {
  authSelectLocale,
  authSelectUser,
} from "reduxFolder/selectors/globalSelectors";
import { createStructuredSelector } from "reselect";
import { BooksVoucherCreateFormProps, VoucherHistoryItem } from "types/books";
import { toastResponseError } from "utils/responseMessageHelper";
import CopyToClipboard from "../../../../components/CopyToClipboard/CopyToClipboard";
import { ButtonColor } from "../../../../enums";
import PaymentButton, {
  PaymentButtonSlot,
} from "../../../../components/PaymentButton";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import { booksCreateVoucher } from "../../../../reduxFolder/reducers/books";
import createBalanceVoucherFormik from "../../helpers/createBalanceVoucherFormik";
import {
  AddFundsContainer,
  AddFundsFormRow,
  BooksBalancePanelSection,
  BooksBalanceLayout,
  BooksVoucherCreateFormWrapper,
  FormWrapper,
  VoucherResponseCodeText,
  VoucherResponseCodeWrapper,
} from "../../styles/BooksAddFunds.style";
import BooksBalance from "../BooksBalance/BooksBalance";

//constants for the range of funds value
const MIN_AMOUNT_ADD_FUNDS = 0.01;
const MAX_AMOUNT_ADD_FUNDS = 10000.0;

const BooksVoucherCreateForm: FC<BooksVoucherCreateFormProps> = React.memo(
  ({ booksCreateVoucher, user, getProfile }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const [voucherResponse, setVoucherResponse] =
      useState<null | VoucherHistoryItem>(null);

    const redirectFunc = (resp: any) => {
      if (resp && resp.value && resp.value.get("data")) {
        setVoucherResponse(resp.value.get("data").toJS());
      }
      getProfile();
    };

    const {
      handleBlur,
      handleSubmit,
      handleChange,
      values,
      errors: { value: amountError },
      touched,
      isSubmitting,
      isValid,
      setFieldValue,
    } = useFormik(
      createBalanceVoucherFormik(
        t,
        booksCreateVoucher,
        redirectFunc,
        currencySymbol,
      ),
    );

    const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) => {
      let inputValue = event.target.value.replace(/[^0-9.]/g, "");
      let numericValue = parseFloat(inputValue);
      setFieldValue("value", inputValue);

      //range of funds value
      if (
        numericValue > MAX_AMOUNT_ADD_FUNDS ||
        numericValue < MIN_AMOUNT_ADD_FUNDS
      ) {
        toastResponseError(
          t("books.errorAmountRangeAddFunds", {
            min: MIN_AMOUNT_ADD_FUNDS,
            max: MAX_AMOUNT_ADD_FUNDS,
            currencySymbol,
          }),
          "error-range-funds",
        );
      }
    };

    const payBtnTitle = `${t("books.voucher.createVoucher")} (${currencySymbol}${values.value})`;

    return (
      <BooksVoucherCreateFormWrapper>
        <FormWrapper>
          <BooksBalancePanelSection>
            <BooksBalanceLayout>
              <BooksBalance balance={user.get("balance")} />
            </BooksBalanceLayout>
          </BooksBalancePanelSection>
          <AddFundsContainer fluid>
            <AddFundsFormRow>
              <Col xs={12}>
                <Input
                  name="value"
                  value={values.value}
                  onChange={handleAmountChange}
                  invalid={touched.value && !!amountError}
                  placeholder={"Enter voucher amount $ 0.00"}
                  tooltip={amountError}
                  type="number"
                />
              </Col>
            </AddFundsFormRow>
            <AddFundsFormRow>
              {voucherResponse && (
                <VoucherResponseCodeWrapper>
                  <CopyToClipboard text={voucherResponse.code}>
                    <VoucherResponseCodeText>
                      {voucherResponse.code}
                    </VoucherResponseCodeText>
                  </CopyToClipboard>
                </VoucherResponseCodeWrapper>
              )}
            </AddFundsFormRow>
          </AddFundsContainer>
        </FormWrapper>
        {voucherResponse && (
          <PaymentButtonSlot>
            <PaymentButton
              color={ButtonColor.Primary}
            >
              <CopyToClipboard text={voucherResponse.code}>
                {`${voucherResponse.code} (${voucherResponse.value}$)`}
              </CopyToClipboard>
            </PaymentButton>
          </PaymentButtonSlot>
        )}
        {!voucherResponse && (
          <PaymentButtonSlot>
            <PaymentButton
              onClick={() => handleSubmit()}
              isLoading={isSubmitting}
              disabled={!isValid || isSubmitting}
            >
              {payBtnTitle}
            </PaymentButton>
          </PaymentButtonSlot>
        )}
      </BooksVoucherCreateFormWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    user: authSelectUser,
    currentLocale: authSelectLocale,
  }),
  {
    booksCreateVoucher,
    getProfile,
  },
);

export default withConnect(BooksVoucherCreateForm);
